<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { copyToClipboard, isNullOrWhitespace } from "@common/Common";
    import { PanelistReadModel } from "@Evaluation/Estimations/Domain/readmodels";
    import { SurveyAccessLinksViewModel } from "@Evaluation/Estimations/Components/SurveyAccessLinks";

    @Component({
        props: {
            viewModel: {
                type: Object,
                required: true
            }
        }
    })
    export default class SurveyAccessLinks extends Vue
    {
        get vm(): SurveyAccessLinksViewModel { return this.$props.viewModel as SurveyAccessLinksViewModel; }
        get opened() : boolean { return this.vm?.opened; }
        get intialized() : boolean { return this.vm.intialized; }
        get panelists() { return this.vm.panelists; }
        
        get step(): string { return this.vm.step; }
        set step(val: string) { this.vm.step = val; }

        recipients: PanelistReadModel[] = null;
        emailSubject: string = "Please complete this survey";
        emailBody: string = "Please click on the following link to complete your survey: {url}";        

        get canSendEmail(): boolean { return isNullOrWhitespace(this.emailBodyError); }
        get emailBodyError(): string { return this.emailBody.indexOf("{url}") === -1 ? "{url} is required in the message. " : null };

        handleSendEmail()
        {
            this.$emit('send-email', {
                SurveyId: this.vm.surveyId,
                EmailSubject: this.emailSubject,
                EmailBody: this.emailBody,
                Recipients: this.recipients
            });
        }

        handleDownloadLinks()
        {
            this.$emit('download-links', this.vm.surveyId);            
        }

        showLinks()
        {
            this.step = "links";
            this.recipients = null;
        }

        emailOne(vm: PanelistReadModel)
        {
            this.recipients = [vm];
            this.step = "email";
        }

        emailAll()
        {
            this.recipients = this.panelists;
            this.step = "email";
        }

        copyAccessUrlToClipboard(rm: PanelistReadModel)
        {
            copyToClipboard(rm.AccessUrl);

            let url = rm.AccessUrl;
            rm.AccessUrl = "Copied to clipboard";

            let handle = setTimeout(() =>
            {
                rm.AccessUrl = url;
                clearTimeout(handle);
            }, 500);
        }

        close()
        {
            this.vm.close();
        }
    }


</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable" v-if="!intialized">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Access links</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <i class="fa fa-spinner fa-spin"></i> Loading...
                    </div>
                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>

            <div class="modal-dialog modal-xl modal-dialog-scrollable" v-if="intialized && step === 'links'">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Access links</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div>
                            <table class="table table-hover">
                                <thead class="thead-light">
                                    <tr>
                                        <th>Panelist</th>
                                        <th>Access link</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="p in panelists" :key="p.ProjectUserId">
                                        <td>
                                            <span>{{p.Name}}</span><br />
                                            <span class="text-muted" v-if="p.HasEmail">{{p.Email}}</span>
                                            <span class="text-danger" v-if="!p.HasEmail">Email not provided</span>
                                        </td>
                                        <td class="align-middle">
                                            <div class="input-group">
                                                <input type="text" class="form-control" readonly v-model="p.AccessUrl" />
                                                <div class="input-group-append">
                                                    <button class="btn btn-outline-secondary" type="button" title="Copy to clipboard" @click="copyAccessUrlToClipboard(p)">
                                                        <i class="fa fa-clipboard" aria-hidden="true"></i>
                                                    </button>
                                                    <button class="btn btn-outline-secondary" type="button" title="Send link via email" :disabled="!p.HasEmail" @click="emailOne(p)">
                                                        <i class="fa-regular fa-envelope"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="handleDownloadLinks" class="btn btn-outline-secondary">Download links</button>
                        <button type="button" @click.prevent="emailAll" class="btn btn-outline-secondary">Email all</button>
                    </div>
                </div>
            </div>

            <div class="modal-dialog modal-xl modal-dialog-scrollable" v-if="intialized && step === 'email'">
                <div  class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Send email ({{recipients.length}})</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <form id="survey-access-links-email-form" @submit.prevent="handleSendEmail">
                            <div class="form-group">
                                <label>Subject:</label>
                                <input type="text" class="form-control" v-model.trim="emailSubject" required />
                            </div>
                            <div class="form-group">
                                <textarea rows="5" class="form-control" v-model.trim="emailBody" required></textarea>
                                <div class="error">{{emailBodyError}}</div>
                            </div>
                        </form>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="showLinks" class="btn btn-outline-secondary">Back</button>
                        <button type="submit" form="survey-access-links-email-form" :disabled="!canSendEmail" class="btn btn-primary">Send email</button>
                    </div>

                </div>
            </div>


        </div>
    </div>
</template>
